import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import commissionService from '../../services/ShipmentCommissionService.js';
import { DollarSign, ChevronDown, ChevronRight, MapPin, Send, Download } from 'lucide-react';
import { PageLayout, Button, StatusBadge } from '../common/index.js';

const EditCommission = () => {
  const { commissionId } = useParams();
  const navigate = useNavigate();

  const [commission, setCommission] = useState(null);
  const [preview, setPreview] = useState(null);
  const [companyProfiles, setCompanyProfiles] = useState([]);
  const [vehicleOwners, setVehicleOwners] = useState([]);
  const [hubData, setHubData] = useState(null);
  const [expandedHubs, setExpandedHubs] = useState({});
  const [activeHubTab, setActiveHubTab] = useState('sender');

  const [formData, setFormData] = useState({
    company_profile_id: '',
    vehicle_owner_id: '',
    company_commission_percent: 30,
    owner_commission_percent: 70,
  });

  const [senderHubPercents, setSenderHubPercents] = useState({});
  const [receiverHubPercents, setReceiverHubPercents] = useState({});

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchAll(); }, [commissionId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [commissionRes, companiesRes, ownersRes] = await Promise.all([
        commissionService.getCommission(commissionId),
        commissionService.getCompanyProfiles(),
        commissionService.getVehicleOwners(),
      ]);

      const data = commissionRes.data.data;
      setCommission(data);
      setCompanyProfiles(Array.isArray(companiesRes.data?.data || companiesRes.data) ? (companiesRes.data?.data || companiesRes.data) : []);
      setVehicleOwners(Array.isArray(ownersRes.data?.data || ownersRes.data) ? (ownersRes.data?.data || ownersRes.data) : []);

      setFormData({
        company_profile_id:        data.company_profile_id        || '',
        vehicle_owner_id:          data.vehicle_owner_id          || '',
        company_commission_percent: parseFloat(data.company_commission_percent) || 30,
        owner_commission_percent:   parseFloat(data.vehicle_owner_commission_percent) || 70,
      });

      // Pre-fill per-hub percents from existing commission data
      const existingSender = data.sender_hub_commissions || [];
      const existingReceiver = data.receiver_hub_commissions || [];
      const sp = {};
      existingSender.forEach(h => { sp[h.hub_id] = parseFloat(h.hub_commission_percent) || 0; });
      const rp = {};
      existingReceiver.forEach(h => { rp[h.hub_id] = parseFloat(h.hub_commission_percent) || 0; });

      const [previewRes, hubsRes] = await Promise.all([
        commissionService.calculatePreview(data.ml_sheet_id),
        commissionService.getHubsByMlSheet(data.ml_sheet_id),
      ]);
      setPreview(previewRes.data?.data || null);
      const hData = hubsRes.data?.data || null;
      setHubData(hData);

      // Merge: use existing percent if available, else 0
      if (hData?.sender_hubs) hData.sender_hubs.forEach(h => { if (!(h.hub_id in sp)) sp[h.hub_id] = 0; });
      if (hData?.receiver_hubs) hData.receiver_hubs.forEach(h => { if (!(h.hub_id in rp)) rp[h.hub_id] = 0; });
      setSenderHubPercents(sp);
      setReceiverHubPercents(rp);
    } catch (err) {
      Alert.error('Failed to load commission');
      navigate('/commissions');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name.includes('percent') ? (parseFloat(value) || 0) : value }));
  };

  const handleSenderHubPercent = (hubId, value) => setSenderHubPercents(p => ({ ...p, [hubId]: parseFloat(value) || 0 }));
  const handleReceiverHubPercent = (hubId, value) => setReceiverHubPercents(p => ({ ...p, [hubId]: parseFloat(value) || 0 }));
  const toggleHubExpand = (key) => setExpandedHubs(p => ({ ...p, [key]: !p[key] }));

  const getCompanyCommissionAmount = () => {
    if (!preview) return 0;
    return (preview.total_freight * (parseFloat(formData.company_commission_percent) || 0)) / 100;
  };
  const calcCompanyCommission = () => getCompanyCommissionAmount().toFixed(2);
  const calcOwnerCommission = () => {
    if (!preview) return '0.00';
    return ((preview.total_freight * (parseFloat(formData.owner_commission_percent) || 0)) / 100).toFixed(2);
  };
  const calcHubAmount = (pct) => (getCompanyCommissionAmount() * (parseFloat(pct) || 0)) / 100;
  const getTotalSenderComm = () => Object.values(senderHubPercents).reduce((s, p) => s + calcHubAmount(p), 0);
  const getTotalReceiverComm = () => Object.values(receiverHubPercents).reduce((s, p) => s + calcHubAmount(p), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_profile_id) { Alert.error('Please select a Company'); return; }
    if (!formData.vehicle_owner_id) { Alert.error('Please select a Vehicle Owner'); return; }

    setSubmitting(true);
    try {
      const buildHubArray = (hubs, percents) =>
        (hubs || []).filter(h => (parseFloat(percents[h.hub_id]) || 0) > 0).map(h => ({
          hub_id: h.hub_id,
          hub_location: h.hub_location,
          commission_percent: parseFloat(percents[h.hub_id]) || 0,
        }));

      await commissionService.updateCommission(commissionId, {
        company_profile_id:         formData.company_profile_id,
        vehicle_owner_id:           formData.vehicle_owner_id,
        company_commission_percent: parseFloat(formData.company_commission_percent) || 30,
        owner_commission_percent:   parseFloat(formData.owner_commission_percent) || 70,
        sender_hub_commissions: buildHubArray(hubData?.sender_hubs, senderHubPercents),
        receiver_hub_commissions: buildHubArray(hubData?.receiver_hubs, receiverHubPercents),
      });
      Alert.success('Commission updated successfully!');
      setTimeout(() => navigate('/commissions'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update commission');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout title="Edit Commission" icon={DollarSign}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading commission...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  const senderHubs = hubData?.sender_hubs || [];
  const receiverHubs = hubData?.receiver_hubs || [];
  const existingSenderHubs = commission?.sender_hub_commissions || [];
  const existingReceiverHubs = commission?.receiver_hub_commissions || [];
  const totalSenderComm = getTotalSenderComm();
  const totalReceiverComm = getTotalReceiverComm();

  const renderHubRow = (hub, type, percents, setPercent) => {
    const key = `${type}-${hub.hub_id}`;
    const isExpanded = expandedHubs[key];
    const pct = percents[hub.hub_id] || 0;
    const amt = calcHubAmount(pct);
    const colorClass = type === 'sender' ? 'purple' : 'indigo';
    const existingHub = (type === 'sender' ? existingSenderHubs : existingReceiverHubs).find(h => h.hub_id === hub.hub_id);

    return (
      <div key={hub.hub_id} className="border-b border-gray-100 last:border-b-0">
        <div className="flex items-center gap-2 px-4 py-3 hover:bg-gray-50 transition-colors">
          <button type="button" onClick={() => toggleHubExpand(key)} className="shrink-0 p-0.5">
            {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          </button>
          <MapPin className={`w-4 h-4 text-${colorClass}-400 shrink-0`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-secondary truncate">{hub.hub_location}</p>
            <p className="text-xs text-gray-400">{hub.hub_id} &middot; {hub.total_shipments} shipment{hub.total_shipments !== 1 ? 's' : ''} &middot; ₹{parseFloat(hub.total_freight || 0).toLocaleString('en-IN')}</p>
            {existingHub && (
              <span className="inline-block mt-1">
                <StatusBadge status={existingHub.payment_status} />{' '}
                <span className="text-xs text-gray-500">
                  Paid: ₹{parseFloat(existingHub.paid_amount || 0).toLocaleString('en-IN')}
                </span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-20">
              <input type="number" value={pct} onChange={(e) => setPercent(hub.hub_id, e.target.value)}
                onFocus={(e) => { if (Number(e.target.value) === 0) e.target.select(); }}
                step="0.01" min="0" max="100" placeholder="0"
                className={`w-full px-2 py-1.5 border border-${colorClass}-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-${colorClass}-500 text-sm text-center bg-white`} />
            </div>
            <span className="text-xs text-gray-400 w-4">%</span>
            <div className={`w-24 px-2 py-1.5 bg-${colorClass}-50 border border-${colorClass}-200 rounded-sm text-sm font-bold text-${colorClass}-800 text-center`}>
              ₹{amt.toFixed(2)}
            </div>
          </div>
        </div>
        {isExpanded && hub.agents && hub.agents.length > 0 && (
          <div className="bg-gray-50 px-4 pb-3 pt-1 ml-8">
            <p className="text-xs font-semibold text-gray-500 mb-2">{type === 'sender' ? 'Agents under this hub:' : 'Users under this hub:'}</p>
            <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">{type === 'sender' ? 'Agent' : 'User'}</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">City</th>
                    {type === 'sender' && <th className="px-3 py-2 text-center font-semibold text-gray-600">Shipments</th>}
                    {type === 'sender' && <th className="px-3 py-2 text-right font-semibold text-gray-600">Freight</th>}
                    <th className="px-3 py-2 text-center font-semibold text-gray-600">Comm. %</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-600">Comm. Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {hub.agents.map(a => {
                    const agentPct = parseFloat(a.agent_commission_percent || 0);
                    const agentAmt = (amt * agentPct) / 100;
                    return (
                      <tr key={a.agent_id}>
                        <td className="px-3 py-2 text-secondary">{a.agent_name} <span className="text-gray-400">({a.agent_id})</span></td>
                        <td className="px-3 py-2 text-gray-500">{a.agent_city || '-'}</td>
                        {type === 'sender' && <td className="px-3 py-2 text-center">{a.booked_shipments}</td>}
                        {type === 'sender' && <td className="px-3 py-2 text-right font-semibold">₹{parseFloat(a.total_freight || 0).toLocaleString('en-IN')}</td>}
                        <td className="px-3 py-2 text-center">{agentPct.toFixed(1)}%</td>
                        <td className="px-3 py-2 text-right font-semibold">₹{agentAmt.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageLayout title="Edit Commission" icon={DollarSign}>
      <form onSubmit={handleSubmit} className="bg-white rounded-card shadow-card p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* Step 1: Info */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Step 1: Commission Info</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-gray-50 p-3 rounded-sm border border-gray-200"><p className="text-xs text-gray-500 mb-1">Commission ID</p><p className="text-sm font-bold text-secondary">{commission.commission_id}</p></div>
                      <div className="bg-gray-50 p-3 rounded-sm border border-gray-200"><p className="text-xs text-gray-500 mb-1">ML Sheet ID</p><p className="text-sm font-bold text-secondary">{commission.ml_sheet_id}</p></div>
                      <div className="bg-brand-light p-3 rounded-sm border border-blue-200"><p className="text-xs text-brand mb-1">Total Freight</p><p className="text-sm font-bold text-secondary">₹{parseFloat(preview?.total_freight || commission.total_freight || 0).toLocaleString('en-IN')}</p></div>
                      <div className="bg-yellow-50 p-3 rounded-sm border border-yellow-200"><p className="text-xs text-yellow-600 mb-1">Total Recovery</p><p className="text-sm font-bold text-secondary">₹{parseFloat(preview?.total_recovery || commission.total_recovery || 0).toLocaleString('en-IN')}</p></div>
                    </div>
                  </div>

                  {/* Step 2: Shipment Details */}
                  {preview && (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Step 2: Shipment Details</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-gray-50 p-3 rounded-sm border border-gray-200"><p className="text-xs text-gray-500 mb-1">To-Pay</p><p className="text-sm font-bold text-secondary">₹{parseFloat(preview.to_pay_freight || 0).toLocaleString('en-IN')}</p></div>
                        <div className="bg-gray-50 p-3 rounded-sm border border-gray-200"><p className="text-xs text-gray-500 mb-1">Paid</p><p className="text-sm font-bold text-secondary">₹{parseFloat(preview.paid_freight || 0).toLocaleString('en-IN')}</p></div>
                        <div className="bg-gray-50 p-3 rounded-sm border border-gray-200"><p className="text-xs text-gray-500 mb-1">HS Charges</p><p className="text-sm font-bold text-secondary">₹{parseFloat(preview.total_hs_charge || 0).toLocaleString('en-IN')}</p></div>
                        <div className="bg-gray-50 p-3 rounded-sm border border-gray-200"><p className="text-xs text-gray-500 mb-1">DCT Charges</p><p className="text-sm font-bold text-secondary">₹{parseFloat(preview.total_dct_charge || 0).toLocaleString('en-IN')}</p></div>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Owner */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Step 3: Vehicle Owner Commission</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Vehicle Owner <span className="text-danger font-bold">*</span></label>
                        <select name="vehicle_owner_id" value={formData.vehicle_owner_id} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base" required>
                          <option value="">Select Vehicle Owner</option>
                          {vehicleOwners.map(o => <option key={o.vehicle_owner_id} value={o.vehicle_owner_id}>{o.owner_name}</option>)}
                        </select>
                        {commission.vehicle_owner_payment_status && (
                          <span className="inline-flex items-center gap-2 mt-2">
                            <StatusBadge status={commission.vehicle_owner_payment_status} />
                            <span className="text-xs text-gray-500">Paid: ₹{parseFloat(commission.vehicle_owner_paid_amount || 0).toLocaleString('en-IN')}</span>
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs sm:text-sm font-medium text-gray-600">Commission %</label>
                        <input type="number" name="owner_commission_percent" value={formData.owner_commission_percent} onChange={handleChange} min="0" max="100" className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base" />
                        <div className="w-full px-3 sm:px-4 py-2 bg-blue-50 border border-gray-300 rounded-sm text-sm font-semibold text-secondary">₹{calcOwnerCommission()}</div>
                      </div>
                    </div>
                  </div>

                  {/* Step 4: Company */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Step 4: Company Commission</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Company <span className="text-danger font-bold">*</span></label>
                        <select name="company_profile_id" value={formData.company_profile_id} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base" required>
                          <option value="">Select Company Profile</option>
                          {companyProfiles.map(c => <option key={c.company_profile_id} value={c.company_profile_id}>{c.company_name}</option>)}
                        </select>
                        {commission.company_payment_status && (
                          <span className="inline-flex items-center gap-2 mt-2">
                            <StatusBadge status={commission.company_payment_status} />
                            <span className="text-xs text-gray-500">Paid: ₹{parseFloat(commission.company_paid_amount || 0).toLocaleString('en-IN')}</span>
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs sm:text-sm font-medium text-gray-600">Commission %</label>
                        <input type="number" name="company_commission_percent" value={formData.company_commission_percent} onChange={handleChange} step="0.01" min="0" max="100" className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base" />
                        <div className="w-full px-3 sm:px-4 py-2 bg-green-50 border border-gray-300 rounded-sm text-sm font-semibold text-secondary">₹{calcCompanyCommission()}</div>
                      </div>
                    </div>
                  </div>

                  {/* Step 5: Hub Commission */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Step 5: Location Hub Commission</h3>

                    <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-5">
                      <p className="text-xs text-amber-800"><span className="font-semibold">Note:</span> Hub commission is calculated as a percentage of the <span className="font-bold">Company Commission Amount (₹{calcCompanyCommission()})</span>. Set commission % individually for each location hub.</p>
                    </div>

                    <div className="flex mb-0 border border-gray-200 rounded-t-sm overflow-hidden">
                      <button type="button" onClick={() => setActiveHubTab('sender')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold transition-all ${activeHubTab === 'sender' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}><Send className="w-4 h-4" /> Sender Hubs ({senderHubs.length})</button>
                      <button type="button" onClick={() => setActiveHubTab('receiver')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold transition-all ${activeHubTab === 'receiver' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}><Download className="w-4 h-4" /> Receiver Hubs ({receiverHubs.length})</button>
                    </div>

                    {activeHubTab === 'sender' && (
                      <div className="border border-t-0 border-purple-200 rounded-b-sm overflow-hidden">
                        <div className="bg-purple-50 px-4 py-2.5 border-b border-purple-200 flex items-center justify-between">
                          <p className="text-xs font-semibold text-purple-700">Base: Company Commission = ₹{calcCompanyCommission()}</p>
                          <div className="flex items-center gap-2"><span className="text-xs text-purple-600">Total:</span><span className="text-sm font-bold text-purple-800 bg-purple-100 px-2 py-0.5 rounded">₹{totalSenderComm.toFixed(2)}</span></div>
                        </div>
                        {senderHubs.length > 0 && <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center gap-2 text-xs font-semibold text-gray-500"><div className="w-5"></div><div className="w-4"></div><div className="flex-1">Hub Location</div><div className="w-20 text-center">Comm. %</div><div className="w-4"></div><div className="w-24 text-center">Amount (₹)</div></div>}
                        {senderHubs.length > 0 ? senderHubs.map(h => renderHubRow(h, 'sender', senderHubPercents, handleSenderHubPercent)) : <div className="p-6 text-center text-sm text-gray-400">No sender hubs found</div>}
                      </div>
                    )}

                    {activeHubTab === 'receiver' && (
                      <div className="border border-t-0 border-indigo-200 rounded-b-sm overflow-hidden">
                        <div className="bg-indigo-50 px-4 py-2.5 border-b border-indigo-200 flex items-center justify-between">
                          <p className="text-xs font-semibold text-indigo-700">Base: Company Commission = ₹{calcCompanyCommission()}</p>
                          <div className="flex items-center gap-2"><span className="text-xs text-indigo-600">Total:</span><span className="text-sm font-bold text-indigo-800 bg-indigo-100 px-2 py-0.5 rounded">₹{totalReceiverComm.toFixed(2)}</span></div>
                        </div>
                        {receiverHubs.length > 0 && <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center gap-2 text-xs font-semibold text-gray-500"><div className="w-5"></div><div className="w-4"></div><div className="flex-1">Hub Location</div><div className="w-20 text-center">Comm. %</div><div className="w-4"></div><div className="w-24 text-center">Amount (₹)</div></div>}
                        {receiverHubs.length > 0 ? receiverHubs.map(h => renderHubRow(h, 'receiver', receiverHubPercents, handleReceiverHubPercent)) : <div className="p-6 text-center text-sm text-gray-400">No receiver hubs found</div>}
                      </div>
                    )}

                    {(totalSenderComm > 0 || totalReceiverComm > 0) && preview && (
                      <div className="mt-5 bg-gray-50 border border-gray-200 rounded-sm p-4">
                        <p className="text-xs font-semibold text-gray-600 mb-3">Hub Commission Preview</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="bg-purple-50 border border-purple-200 rounded-sm p-3 text-center"><p className="text-xs text-purple-600 mb-1">Total Sender Hub Commission</p><p className="text-lg font-bold text-purple-800">₹{totalSenderComm.toFixed(2)}</p></div>
                          <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3 text-center"><p className="text-xs text-indigo-600 mb-1">Total Receiver Hub Commission</p><p className="text-lg font-bold text-indigo-800">₹{totalReceiverComm.toFixed(2)}</p></div>
                          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-center"><p className="text-xs text-blue-600 mb-1">Grand Total Hub Commission</p><p className="text-lg font-bold text-blue-800">₹{(totalSenderComm + totalReceiverComm).toFixed(2)}</p></div>
                        </div>
                      </div>
                    )}
                  </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <Button type="submit" loading={submitting}>
            {submitting ? 'Updating...' : 'Update Commission'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/commissions')}>
            Cancel
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

export default EditCommission;
